import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import CountStepper, { MIN_COUNT } from './CountStepper';
import { LAUNCHER_HEIGHT, SYMBOL_SLOT, TILE_HEIGHT } from './tileMetrics';
import { useTileColours } from './tileTheme';
import { KeyCombo } from '../../../pedigree-tool/canvas-tools/KeyCap';
import { useShortcutsEnabled } from '../../../pedigree-tool/shortcutsEnabledStore';

/**
 * One add-a-relative tile: pedigree symbol, sentence-case label, and a count
 * stepper that appears on hover or keyboard focus.
 *
 * Deliberately dumb. It emits `onAdd(count)` and knows nothing about spouses,
 * batches or the pedigree — the container scopes the add and writes the
 * confirmation. That is what lets the same tile serve siblings and children.
 *
 * COLOUR follows the sidebar accordion rows this panel sits under: light and
 * neutral at rest with the accordion's navy label, then a faint accent tint on
 * hover with the brand teal on the glyph and the AA-safe darkened teal on the
 * label. See tileTheme.js — the values are re-exported from SidebarSection so the
 * two surfaces cannot drift.
 *
 * A previous pass filled these with `palette.secondary`, the configured button
 * colour. On a deployment whose button colour is a dark navy that produced a wall
 * of fifteen dark blocks that competed with the pedigree itself. Neutral-at-rest is
 * the point: the panel should recede until you reach for it.
 *
 * DISABLED tiles stay outlined with `text.secondary` rather than being greyed on a
 * fill — a disabled contained button is white-on-pale-grey, the exact 4.5:1 failure
 * the old panel had.
 *
 * STRUCTURE MATTERS HERE. The add control is a `button`, and the stepper is its
 * SIBLING inside a positioned wrapper — never a child. Nesting the stepper's two
 * IconButtons inside the tile's button is invalid HTML (`<button>` cannot contain
 * `<button>`), and browsers recover from it by dropping the inner controls out of
 * the tab order, which is exactly the keyboard reachability this panel has to
 * have. The wrapper carries hover and focus so the stepper still appears when
 * either the tile or the stepper itself is focused.
 *
 * @param {Object}   props
 * @param {React.ReactNode} props.symbol      Notation glyph (aria-hidden).
 * @param {string}   props.label              Visible label, sentence case.
 * @param {string}   props.accessibleName     Fully-qualified name — "Add son with
 *                                            Jane Doe", not "Son". The visible
 *                                            label alone is ambiguous once the
 *                                            child scope can change under it.
 * @param {Function} props.onAdd              Called with the pending count.
 * @param {boolean}  [props.disabled]
 * @param {string}   [props.disabledReason]   Shown as a tooltip AND appended to
 *                                            the accessible name. An unavailable
 *                                            control always says why.
 * @param {boolean}  [props.showStepper=true] False removes the stepper entirely.
 *                                            The grid must be unchanged when it
 *                                            is — guarded by a test.
 * @param {'tile'|'row'} [props.layout='tile'] 'row' lays the glyph beside the label
 *                                            at LAUNCHER_HEIGHT, for a full-width
 *                                            control that has to line up with the
 *                                            launcher rows next to it (Unknown
 *                                            person, in Other). Same colour and
 *                                            same behaviour — only the arrangement
 *                                            changes, because a 68px tile stretched
 *                                            across the whole panel reads as a
 *                                            banner rather than a button.
 * @param {string}   [props.vaAnchor]         Copilot walkthrough anchor.
 * @param {string[]} [props.shortcut]         Keyboard binding, from
 *                                            ADD_RELATIVE_KEYS. Revealed on
 *                                            hover/focus, like the stepper.
 */
const RelativeTile = ({
  symbol,
  label,
  accessibleName,
  onAdd,
  disabled = false,
  disabledReason = '',
  showStepper = true,
  layout = 'tile',
  shortcut = null,
  vaAnchor
}) => {
  const c = useTileColours();
  // The caps follow the canvas toolbar's keyboard-shortcuts toggle: with shortcuts
  // off the keys do nothing, so advertising them would be wrong.
  const shortcutsOn = useShortcutsEnabled();
  const showKeys = !!shortcut && shortcutsOn;
  const [count, setCount] = React.useState(MIN_COUNT);
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);

  // A count above 1 stays pinned so the user can see what the next click will do,
  // then resets after the add — TGCLOUD-742 was exactly this leaking to the next
  // person ("inadvertently add 3 sisters to next relative"). The container
  // remounts the tile when the selection moves, which resets it there too.
  const pinned = count > MIN_COUNT;

  const handleAdd = () => {
    if (disabled) return;
    onAdd(count);
    setCount(MIN_COUNT);
  };

  const name = disabledReason
    ? `${accessibleName || label}, ${disabledReason}`
    : accessibleName || label;

  const isRow = layout === 'row';

  const tile = (
    <Box
      // React's onFocus/onBlur bubble, so this gives focus-within behaviour
      // across the add button AND the stepper without a second listener.
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      // height:100% all the way down is what holds a row level. A grid stretches
      // its items, but a minHeight-only child does not fill the space it is given,
      // so the tallest control in a row used to set the row height and the others
      // floated shorter inside it.
      sx={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <Box
        component="button"
        type="button"
        aria-label={name}
        aria-disabled={disabled ? 'true' : undefined}
        data-va-anchor={vaAnchor}
        disabled={disabled}
        onClick={handleAdd}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: isRow ? LAUNCHER_HEIGHT : TILE_HEIGHT,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: isRow ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: isRow ? 'flex-start' : 'center',
          // Room to breathe between the notation and its label; the first pass had
          // them almost touching.
          gap: isRow ? 1.25 : 0.75,
          px: isRow ? 1.5 : 1,
          py: isRow ? 0.75 : 1.25,
          font: 'inherit',
          cursor: disabled ? 'default' : 'pointer',
          borderRadius: 1.5,
          border: 1,
          transition:
            'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
          ...(disabled
            ? {
                borderColor: 'divider',
                bgcolor: 'background.paper',
                color: 'text.secondary'
              }
            : {
                borderColor: 'divider',
                bgcolor: 'background.paper',
                color: c.restText,
                // The accordion's own hover: faint accent tint, accent border, and
                // the accent carried onto the glyph while the small label takes the
                // darkened AA-safe teal.
                '&:hover': {
                  bgcolor: c.hoverTint,
                  borderColor: c.hoverBorder,
                  color: c.hoverText,
                  '& .tile-glyph': { color: c.hoverGlyph }
                }
              }),
          '&:focus-visible': {
            outline: 2,
            outlineColor: c.accent,
            outlineOffset: 2,
            bgcolor: disabled ? undefined : c.hoverTint,
            '& .tile-glyph': disabled ? undefined : { color: c.hoverGlyph }
          }
        }}
      >
        <Box
          aria-hidden="true"
          className="tile-glyph"
          sx={{
            display: 'flex',
            transition: 'color 120ms ease',
            alignItems: 'center',
            justifyContent: 'center',
            // Fixed, so a short glyph (the joined spouse pair) and a tall one (a
            // lone square) produce the same tile height. See SYMBOL_SLOT.
            height: SYMBOL_SLOT,
            flexShrink: 0
          }}
        >
          {symbol}
        </Box>
        <Typography
          component="span"
          sx={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.2,
            ...(isRow ? { flex: 1, textAlign: 'left' } : {})
          }}
        >
          {label}
        </Typography>
        {/* A row has the width to spare and no stepper to compete with, so its cap
            is an ordinary flex child and always visible. */}
        {showKeys && isRow && <KeyCombo keys={shortcut} size="sm" />}
      </Box>
      {/* The keyboard binding. Every add has had one since TGCLOUD-1541 and nothing
          in the app said so (TGCLOUD-1642's footnote).

          ABSOLUTELY POSITIONED, and bottom-right specifically: it must not affect
          layout, because panelHeight.test.jsx derives the panel's height from
          TILE_HEIGHT — and CountStepper already owns top-right on exactly the four
          tiles that have keys.

          Revealed on hover/focus, on the same trigger and for the same reason as the
          stepper: eight permanent caps in a 400px sidebar is clutter, and a cap that
          appears where the pointer already is still closes the gap. */}
      {showKeys && !isRow && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 2,
            right: 4,
            pointerEvents: 'none',
            opacity: hovered || focused ? 1 : 0,
            transition: 'opacity 120ms ease'
          }}
        >
          <KeyCombo keys={shortcut} size="sm" />
        </Box>
      )}
      {showStepper && (
        <CountStepper
          label={label}
          value={count}
          onChange={setCount}
          disabled={disabled}
          visible={hovered || focused || pinned}
        />
      )}
    </Box>
  );

  // Wrapped in a span because MUI cannot attach a tooltip to a disabled button.
  // It has to stretch too, or a tile with a reason sits shorter than its row.
  return disabledReason ? (
    <Tooltip title={disabledReason} placement="top">
      <span style={{ display: 'block', width: '100%', height: '100%' }}>
        {tile}
      </span>
    </Tooltip>
  ) : (
    tile
  );
};

export default RelativeTile;
